import { inject, Injectable } from '@angular/core';
import { angularjs } from '@app/service/migration-helpers/angularjs.injection';
import { WINDOW } from '@app/core/window.token';
import type { IContentStore } from '@app/interface/content/content-store.interface';
import type { ILoggerService } from '@app/service/logger.service';
import type { AddressConsistencyService } from '@app/interface/service/address-consistency-service.interface';
import type {
  AddressValidationRequest,
  AddressValidationResponse,
  AddressValidationService
} from '@app/interface/validation/address-validation-service.interface';
import type { ApplePayErrorCode as BraintreeApplePayErrorCode } from 'braintree-web/apple-pay';

const MELISSA_CITY_CODES = ['AE05', 'AE11'];
const MELISSA_STATE_CODES = ['AE12'];
const MELISSA_POSTAL_CODES = ['AE13'];

export enum ApplePayErrorCode {
  ShippingContactInvalid = 'shippingContactInvalid',
  BillingContactInvalid = 'billingContactInvalid',
  AddressUnserviceable = 'addressUnserviceable'
}

enum ApplePayTranslationKey {
  AddressRestrictionBanner = 'BILLING_INFO_ADDRESS_RESTRICTION_BANNER'
}

@Injectable()
export class ApplePayAddressService {
  private readonly contentStore: IContentStore = inject(angularjs.ContentStore);
  private readonly addressValidationService: AddressValidationService = inject(angularjs.AddressValidationService);
  private readonly addressConsistencyService: AddressConsistencyService = inject(angularjs.AddressConsistencyService);
  private readonly loggerService: ILoggerService = inject(angularjs.loggerService);
  private readonly window: Window = inject(WINDOW);

  async validateAddress(
    paymentAddress: ApplePayJS.ApplePayPaymentContact | undefined,
    shippingAddressRequired: boolean
  ): Promise<{ valid: boolean; errors: any[] }> {
    if (shippingAddressRequired && this.isRestrictedPoBox(paymentAddress)) {
      return {
        valid: false,
        errors: [
          this.createUnserviceableError(this.contentStore.translate(ApplePayTranslationKey.AddressRestrictionBanner))
        ]
      };
    }

    const config = this.contentStore.getConfig();
    if (!config.addressValidationEnabled) {
      return { valid: true, errors: [] };
    }

    const melissaAddress: AddressValidationRequest = {
      a1: paymentAddress?.addressLines?.[0],
      a2: paymentAddress?.addressLines?.[1],
      city: paymentAddress?.locality,
      state: { name: paymentAddress?.administrativeArea },
      postalCode: paymentAddress?.postalCode
    };

    const contactCode = shippingAddressRequired
      ? ApplePayErrorCode.ShippingContactInvalid
      : ApplePayErrorCode.BillingContactInvalid;

    try {
      const record: AddressValidationResponse = await this.addressValidationService.validateAddress(melissaAddress);
      const codes = record?.Results ? record.Results.split(',') : [];
      const errors = this.buildApplePayErrors(codes, contactCode, config.melissaErrors, shippingAddressRequired);
      return { valid: errors.length === 0, errors };
    } catch (err: any) {
      this.loggerService.error(err, { cause: 'Apple Pay Melissa address validation error' });
      return {
        valid: false,
        errors: [new this.window.ApplePayError(contactCode, undefined, 'Unable to verify address, please try again')]
      };
    }
  }

  private isRestrictedPoBox(paymentAddress: ApplePayJS.ApplePayPaymentContact | undefined): boolean {
    const stateProvince = this.contentStore.getEnum('StateProvince')?.code?.[paymentAddress?.administrativeArea ?? ''];

    return (paymentAddress?.addressLines ?? []).some((addressLine) =>
      this.addressConsistencyService.isAddressRestricted(stateProvince, addressLine)
    );
  }

  /**
   * Checks that an Apple Pay contact carries every field MyLifetouch requires
   * (mirrors `requiredShippingContactFields`/`requiredBillingContactFields` in
   * apple-pay-checkout.service.ts: name, email, phone, postalAddress).
   */
  hasRequiredAddressFields(address: ApplePayJS.ApplePayPaymentContact | undefined): boolean {
    if (!address) {
      return false;
    }

    return Boolean(
      address.givenName &&
      address.familyName &&
      address.emailAddress &&
      address.phoneNumber &&
      address.addressLines?.[0] &&
      address.locality &&
      address.administrativeArea &&
      address.postalCode &&
      address.countryCode
    );
  }

  /**
   * Maps a MyLifetouch address (shipping/billing address on file) to an
   * Apple Pay payment contact, used to prefill the Apple Pay sheet.
   */
  toApplePay(address: Partial<any> | null): ApplePayJS.ApplePayPaymentContact {
    if (!address?.postalCode) {
      return {};
    }
    return {
      emailAddress: address.email,
      givenName: address.firstName,
      familyName: address.lastName,
      addressLines: [address.streetAddress ?? '', address.aptSuite ?? ''],
      locality: address.city,
      postalCode: address.postalCode,
      phoneNumber: address.phoneNumber,
      administrativeArea: address.stateProvince?.name,
      countryCode: address.stateProvince?.country
    };
  }

  /**
   * Maps an Apple Pay payment contact back to a MyLifetouch address, used when
   * persisting the contact the customer picked/confirmed in the Apple Pay sheet.
   */
  fromApplePay(applePayAddress: ApplePayJS.ApplePayPaymentContact | undefined): Partial<any> {
    if (!applePayAddress?.postalCode) {
      return {};
    }
    return {
      email: applePayAddress.emailAddress,
      firstName: applePayAddress.givenName,
      lastName: applePayAddress.familyName,
      streetAddress:
        applePayAddress.addressLines && applePayAddress.addressLines.length > 0
          ? applePayAddress.addressLines[0]
          : null,
      aptSuite:
        applePayAddress.addressLines && applePayAddress.addressLines.length > 1
          ? applePayAddress.addressLines[1]
          : null,
      city: applePayAddress.locality,
      postalCode: applePayAddress.postalCode,
      phoneNumber: applePayAddress.phoneNumber,
      stateProvince: applePayAddress.administrativeArea,
      country: applePayAddress.countryCode
    };
  }

  buildApplePayErrors(
    resultCodes: string[],
    contactCode: BraintreeApplePayErrorCode,
    melissaErrors: any,
    shippingAddressRequired: boolean
  ): any[] {
    const errors: any[] = [];

    if (resultCodes.some((code) => MELISSA_CITY_CODES.includes(code))) {
      errors.push(new this.window.ApplePayError(contactCode, 'locality', 'City could not be verified'));
    }

    if (resultCodes.some((code) => MELISSA_STATE_CODES.includes(code))) {
      errors.push(new this.window.ApplePayError(contactCode, 'administrativeArea', 'State could not be verified'));
    }

    if (resultCodes.some((code) => MELISSA_POSTAL_CODES.includes(code))) {
      errors.push(new this.window.ApplePayError(contactCode, 'postalCode', "ZIP code doesn't match city and state"));
    }

    const streetCodes = resultCodes.filter(
      (code) =>
        melissaErrors.address1.includes(code) &&
        !MELISSA_CITY_CODES.includes(code) &&
        !MELISSA_STATE_CODES.includes(code) &&
        !MELISSA_POSTAL_CODES.includes(code)
    );
    if (streetCodes.length > 0) {
      errors.push(new this.window.ApplePayError(contactCode, 'addressLines', 'Street address could not be verified'));
    }

    if (resultCodes.some((code) => melissaErrors.address2.includes(code))) {
      errors.push(new this.window.ApplePayError(contactCode, 'addressLines', 'Apartment or suite not found'));
    }

    if (errors.length === 0 && resultCodes.some((code) => melissaErrors.generic.includes(code))) {
      errors.push(new this.window.ApplePayError(contactCode, 'postalAddress', 'Please verify your address'));
    }

    if (errors.length === 0 && shippingAddressRequired && resultCodes.some((code) => code.startsWith('AE'))) {
      errors.push(this.createUnserviceableError('We are unable to ship to this address'));
    }

    return errors;
  }

  private createUnserviceableError(message: string): ApplePayJS.ApplePayError {
    return new this.window.ApplePayError(ApplePayErrorCode.AddressUnserviceable, undefined, message);
  }
}
